import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Checkbox } from '@/components/ui/checkbox'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { ClipboardCheck, AlertCircle, CheckCircle } from 'lucide-react'

interface DefectItem {
  category: string
  item: string
  hasDefect: boolean
  notes?: string
}

const inspectionCategories = [
  {
    name: 'Exterior',
    items: ['Lights (headlights, taillights, turn signals)', 'Mirrors', 'Windshield / Wipers', 'Tires / Wheels', 'Body / Doors']
  },
  {
    name: 'Engine Compartment',
    items: ['Oil Level', 'Coolant Level', 'Belts / Hoses', 'Battery', 'Leaks']
  },
  {
    name: 'Brakes',
    items: ['Brake Pedal', 'Parking Brake', 'Brake Fluid', 'Air Pressure (if applicable)']
  },
  {
    name: 'Interior / Cab',
    items: ['Gauges / Instruments', 'Steering', 'Horn', 'Seat Belts', 'Fire Extinguisher']
  },
  {
    name: 'Safety Equipment',
    items: ['Emergency Triangles / Flares', 'Spare Fuses', 'Reflectors']
  }
]

export function DVIRForm() {
  const queryClient = useQueryClient()
  const [inspectionType, setInspectionType] = useState<'pre_trip' | 'post_trip'>('pre_trip')
  const [selectedVehicle, setSelectedVehicle] = useState('')
  const [defects, setDefects] = useState<DefectItem[]>([])
  const [generalNotes, setGeneralNotes] = useState('')
  const [odometer, setOdometer] = useState('')

  const submitMutation = useMutation({
    mutationFn: async (data: {
      vehicle_id: string
      inspection_type: string
      defects: DefectItem[]
      notes: string
      odometer: number
    }) => {
      const res = await fetch('/api/eld/dvir', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      })
      if (!res.ok) throw new Error('Failed to submit DVIR')
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dvir-reports'] })
      // Reset form
      setDefects([])
      setGeneralNotes('')
      setOdometer('')
    }
  })

  const handleDefectToggle = (category: string, item: string, checked: boolean) => {
    setDefects(prev => {
      const existing = prev.find(d => d.category === category && d.item === item)
      if (existing) {
        if (!checked) {
          return prev.filter(d => !(d.category === category && d.item === item))
        }
        return prev
      }
      if (checked) {
        return [...prev, { category, item, hasDefect: true }]
      }
      return prev
    })
  }

  const handleDefectNotes = (category: string, item: string, notes: string) => {
    setDefects(prev =>
      prev.map(d =>
        d.category === category && d.item === item
          ? { ...d, notes }
          : d
      )
    )
  }

  const handleSubmit = () => {
    if (!selectedVehicle) {
      alert('Please select a vehicle')
      return
    }

    submitMutation.mutate({
      vehicle_id: selectedVehicle,
      inspection_type: inspectionType,
      defects,
      notes: generalNotes,
      odometer: parseInt(odometer) || 0
    })
  }

  const hasDefects = defects.length > 0
  const inspectionStatus = hasDefects ? 'fail' : 'pass'

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5" />
              Driver Vehicle Inspection Report (DVIR)
            </CardTitle>
            <CardDescription>
              Complete inspection before and after each trip
            </CardDescription>
          </div>
          {inspectionStatus && (
            <Badge variant={inspectionStatus === 'pass' ? 'default' : 'destructive'} className="text-sm">
              {inspectionStatus === 'pass' ? (
                <>
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Pass
                </>
              ) : (
                <>
                  <AlertCircle className="h-3 w-3 mr-1" />
                  Defects Found
                </>
              )}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Basic Information */}
        <div className="grid gap-4 md:grid-cols-3">
          <div className="space-y-2">
            <Label>Inspection Type</Label>
            <RadioGroup value={inspectionType} onValueChange={(v: string) => setInspectionType(v as 'pre_trip' | 'post_trip')}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="pre_trip" id="pre_trip" />
                <Label htmlFor="pre_trip" className="font-normal cursor-pointer">Pre-Trip</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="post_trip" id="post_trip" />
                <Label htmlFor="post_trip" className="font-normal cursor-pointer">Post-Trip</Label>
              </div>
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label htmlFor="vehicle">Vehicle</Label>
            <Select value={selectedVehicle} onValueChange={setSelectedVehicle}>
              <SelectTrigger id="vehicle">
                <SelectValue placeholder="Select vehicle" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="veh_001">Truck 001 (ABC123)</SelectItem>
                <SelectItem value="veh_002">Van 002 (XYZ789)</SelectItem>
                <SelectItem value="veh_003">Truck 003 (DEF456)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="odometer">Odometer Reading</Label>
            <input
              id="odometer"
              type="number"
              value={odometer}
              onChange={(e) => setOdometer(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              placeholder="125000"
            />
          </div>
        </div>

        <Separator />

        {/* Inspection Categories */}
        <div className="space-y-6">
          {inspectionCategories.map((category) => (
            <div key={category.name}>
              <h3 className="font-semibold text-sm mb-3 text-primary">{category.name}</h3>
              <div className="space-y-3 ml-4">
                {category.items.map((item) => {
                  const defect = defects.find(d => d.category === category.name && d.item === item)
                  const hasDefect = !!defect

                  return (
                    <div key={item} className="space-y-2">
                      <div className="flex items-start space-x-3">
                        <Checkbox
                          id={`${category.name}-${item}`}
                          checked={hasDefect}
                          onCheckedChange={(checked: boolean) => handleDefectToggle(category.name, item, checked)}
                        />
                        <Label
                          htmlFor={`${category.name}-${item}`}
                          className="text-sm font-normal cursor-pointer leading-5"
                        >
                          {item}
                        </Label>
                      </div>
                      {hasDefect && (
                        <Textarea
                          placeholder="Describe the defect..."
                          value={defect.notes || ''}
                          onChange={(e) => handleDefectNotes(category.name, item, e.target.value)}
                          className="ml-7 h-20"
                        />
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
        </div>

        <Separator />

        {/* General Notes */}
        <div className="space-y-2">
          <Label htmlFor="notes">Additional Notes</Label>
          <Textarea
            id="notes"
            value={generalNotes}
            onChange={(e) => setGeneralNotes(e.target.value)}
            placeholder="Any additional observations or comments..."
            className="h-24"
          />
        </div>

        {/* Submit */}
        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={() => {
              setDefects([])
              setGeneralNotes('')
              setOdometer('')
            }}
          >
            Clear
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitMutation.isPending || !selectedVehicle}
          >
            {submitMutation.isPending ? 'Submitting...' : 'Submit DVIR'}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
